<template>
  <div class="w-full border rounded overflow-hidden bg-gray-50 dark:bg-gray-900">
    <div class="w-full px-3 py-2 flex items-center justify-between">
      <div>
        <div v-if="orders.loading || isFiltering" class="flex items-center">
          <o-loader
            :scale="0.3"
            class="h-8 w-8 mr-4"
          />
          <span class="align-middle">{{ $t('general.loading') }}</span>
        </div>
        <h4 v-else class="font-semibold text-sm leading-none text-gray-800 dark:text-gray-100">
          <span>{{ $t('components.order.elements.selectMulti.selected', { count: model.length }) }}</span>
        </h4>
      </div>

      <div class="flex-grow flex items-center justify-end space-x-2">
        <o-filters :filters="filters" />
      </div>
    </div>

    <div class="border-t px-3 py-2">
      <o-field-group
        :name="$t('order.dateRange')"
        :status="datesValidation ? datesValidation.status : null"
        :message="datesValidation ? datesValidation.message : null"
      >
        <order-date-range
          v-model="filters.filterStates.dates.value"
          :status="datesValidation ? datesValidation.status : null"
        />
      </o-field-group>
    </div>

    <div class="border-t">
      <div class="h-full bg-white overflow-hidden flex flex-col dark:bg-gray-900">
        <div class="flex items-center justify-between px-3 py-2 border-b bg-gray-50 dark:bg-gray-900">
          <div class="flex items-center space-x-2">
            <p class="text-sm text-gray-600 font-medium leading-none dark:text-gray-300">
              {{ $t('components.order.elements.selectMulti.order') }}
            </p>
            <p class="text-xs text-gray-500 dark:text-gray-400">
              {{ $t('components.order.elements.selectMulti.pageOf', { page: orders.page || 1, pages: orders.pages || 1 }) }}
            </p>
          </div>

          <div class="flex items-center space-x-2">
            <label class="text-sm text-gray-600 dark:text-gray-300">{{ $t('components.order.elements.selectMulti.selectAllResults') }}</label>
            <input
              ref="select-all"
              type="checkbox"
              :checked="selectingAllResults || (!!orders.total && orders.total === model.length)"
              :disabled="selectingAllLoading || !orders.total"
              class="o-checkbox appearance-none h-4 w-4 border border-gray-300 rounded  checked:bg-blue-600 checked:border-blue-600"
              @click="handleSelectAll"
            >
            <o-loader v-if="selectingAllLoading" :scale="0.2" class="h-4 w-4" />
          </div>
        </div>
        <o-table
          :data="orders"
          :limit="limit"
          scroll
          container-class="overflow-hidden"
          class="h-full"
        >
          <template #row="{ row }">
            <checkbox-field
              v-model="model"
              :value="row"
              :label="row.name"
              option-class="py-1 px-3 flex flex-row-reverse justify-between items-center rounded transition duration-200"
            >
              <div class="flex items-center justify-between w-full">
                <div class="flex items-center space-x-2">
                  <div class="text-sm">
                    <p class="text-gray-800 dark:text-gray-100">
                      {{ row.customer.displayName }} - {{ row.orderNumber }}
                    </p>
                    <p class="text-gray-600 dark:text-gray-300">
                      {{ row.dateLabel }}
                    </p>
                  </div>
                </div>
              </div>
            </checkbox-field>
          </template>
        </o-table>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import debounce from 'lodash/debounce'

import OrderDateRange from '@/components/order/elements/DateRange.vue'

import {
  OTable,
  OFilters,
  OFieldGroup,
} from '@/components/elements'

import {
  CheckboxField,
} from '@/components/fields'

import {
  TablePager,
} from '@/_api/lib/pager'

import { useOrderFilters } from '~/composables/filters/useOrderFilters'

import { useServiceStore } from '~/stores/useServiceStore'
import { useEmployeeStore } from '~/stores/useEmployeeStore'
import { useProductStore } from '~/stores/useProductStore'

const { $api, $date, $notification } = useNuxtApp()

const serviceStore = useServiceStore()
const employeeStore = useEmployeeStore()
const productStore = useProductStore()

const model = defineModel<any[]>({ default: () => [] })

const filters = reactive(useOrderFilters())
const isFiltering = ref(false)
const limit = ref(50)
const selectingAllResults = ref(false)
const selectingAllLoading = ref(false)
const orders = ref(new TablePager((page: number) => {
  return $api.order.filter({ page, limit: limit.value, filters: filters.getFilterParams() })
}, limit.value))

const employees = computed(() => employeeStore.all)
const services = computed(() => serviceStore.categorised)
const products = computed(() => productStore.all)

const datesValidation = computed(() => filters.getValidation('dates'))

const filterValues = computed(() => {
  const values: Record<string, any> = {}
  for (const [key, state] of Object.entries(filters.filterStates)) {
    values[key] = (state as any).value
  }
  return values
})

watch(filterValues, debounce(() => {
  if (selectingAllResults.value) {
    model.value = []
    selectingAllResults.value = false
  }
  orders.value.reload()
}, 500), { deep: true })

watch(employees, () => { filters.updateEmployeeOptions() })
watch(services, () => { filters.updateServiceOptions() })
watch(products, () => { filters.updateProductOptions() })

onMounted(async () => {
  serviceStore.sync()
  employeeStore.sync()
  productStore.sync()

  const date = new Date()
  const from = new Date(date.getFullYear(), date.getMonth(), 1)
  const to = new Date(date.getFullYear(), date.getMonth() + 1, 0)

  filters.filterStates.dates.value = {
    start: $date.format(from),
    finish: $date.format(to),
  }

  await orders.value.load()
})

function reload() {
  orders.value.reload()
}

async function handleSelectAll() {
  await handleSelectAllResults()
}

async function handleSelectAllResults() {
  if (selectingAllResults.value) {
    model.value = []
    selectingAllResults.value = false
    return
  }

  selectingAllLoading.value = true

  try {
    const firstPage = await $api.order.filter({ page: 1, limit: limit.value, filters: filters.getFilterParams() })
    const pageCount = firstPage?.paging?.pageCount || 1
    const byId = new Map()

    for (const item of (firstPage?.items || [])) {
      if (item?.idOrder) {
        byId.set(item.idOrder, item)
      }
    }

    for (let page = 2; page <= pageCount; page++) {
      const response = await $api.order.filter({ page, limit: limit.value, filters: filters.getFilterParams() })

      for (const item of (response?.items || [])) {
        if (item?.idOrder) {
          byId.set(item.idOrder, item)
        }
      }
    }

    model.value = Array.from(byId.values())
    selectingAllResults.value = true
  } catch (e: any) {
    console.log(e)
    $notification.error({ errors: e?.errors })
  } finally {
    selectingAllLoading.value = false
  }
}

defineExpose({ reload })
</script>
